/**
 * Types para gerenciamento de templates da API Oficial
 */

// Status possíveis de um template
export type TemplateStatus = 
  | 'PENDING'      // Em análise
  | 'APPROVED'     // Aprovado
  | 'REJECTED'     // Rejeitado
  | 'DISABLED'     // Desabilitado
  | 'PAUSED';      // Pausado

// Categorias de template
export type TemplateCategory = 
  | 'MARKETING'        // Promoções, ofertas
  | 'UTILITY'          // Confirmações, atualizações
  | 'AUTHENTICATION';  // OTP, códigos de verificação

// Tipos de componente
export type ComponentType = 
  | 'HEADER' 
  | 'BODY' 
  | 'FOOTER' 
  | 'BUTTONS';

// Formatos de header
export type HeaderFormat = 
  | 'TEXT' 
  | 'IMAGE' 
  | 'VIDEO' 
  | 'DOCUMENT';

// Tipos de botão
export type ButtonType = 
  | 'QUICK_REPLY'   // Resposta rápida
  | 'URL'           // Link
  | 'PHONE_NUMBER'; // Telefone

/**
 * Botão de template
 */
export interface TemplateButton {
  type: ButtonType;
  text: string;
  url?: string;
  phone_number?: string;
  example?: string[];
}

/**
 * Componente de template
 */
export interface TemplateComponent {
  type: ComponentType;
  format?: HeaderFormat;
  text?: string;
  buttons?: TemplateButton[];
  example?: {
    header_text?: string[];
    header_handle?: string[];
    body_text?: string[][];
  };
}

/**
 * Template completo
 */
export interface WhatsAppTemplate {
  id: string;
  name: string;
  category: TemplateCategory;
  language: string;
  status: TemplateStatus;
  components: TemplateComponent[];
  rejected_reason?: string;
  previous_category?: string;
}

// Alias para facilitar uso
export type OfficialApiTemplate = WhatsAppTemplate;

/**
 * Dados para criar template
 */
export interface CreateTemplateData {
  name: string;
  language: string;
  category: TemplateCategory;
  components: TemplateComponent[];
  allow_category_change?: boolean;
}

/**
 * Tipos de qualidade do número WhatsApp
 */
export type PhoneNumberQuality = 
  | 'GREEN'    // Alta qualidade
  | 'YELLOW'   // Média qualidade
  | 'RED'      // Baixa qualidade
  | 'UNKNOWN'; // Desconhecido

/**
 * Status de verificação do número
 */
export type PhoneNumberStatus = 
  | 'CONNECTED'      // Conectado
  | 'DISCONNECTED'   // Desconectado
  | 'FLAGGED'        // Sinalizado
  | 'UNKNOWN';       // Desconhecido

/**
 * Inbox (caixa de entrada) da API Oficial
 */
export interface OfficialApiInbox {
  id: number;
  name: string;
  phoneNumber: string;
  channelId: number;
  hasTemplates: boolean;
  providerConfig: {
    apiKey: string;              // WhatsApp API Token (vem da API HTTP)
    phoneNumberId: string;
    businessAccountId: string;
    appId?: string;              // APP_ID da Meta (configurado pelo usuário)
  };
  // Informações de saúde do número
  qualityRating?: PhoneNumberQuality;
  status?: PhoneNumberStatus;
}

/**
 * Resposta da API de listagem de templates
 */
export interface TemplateListResponse {
  success: boolean;
  data: WhatsAppTemplate[];
  total: number;
  paging?: {
    cursors?: {
      before?: string;
      after?: string;
    };
    next?: string;
  };
}

/**
 * Resposta de sincronização
 */
export interface SyncTemplatesResponse {
  success: boolean;
  data: {
    total: number;
    statusCount: {
      [key: string]: number;
    };
    templates: WhatsAppTemplate[];
  };
  message: string;
}

/**
 * Estatísticas de templates
 */
export interface TemplateStats {
  total: number;
  byStatus: {
    [key in TemplateStatus]?: number;
  };
  byCategory: {
    [key in TemplateCategory]?: number;
  };
  byLanguage: {
    [language: string]: number;
  };
}

/**
 * Resposta de validação
 */
export interface TemplateValidationResponse {
  success: boolean;
  valid: boolean;
  errors?: string[];
  warnings?: string[];
  message?: string;
}

/**
 * Filtros para templates
 */
export interface TemplateFilters {
  status?: TemplateStatus;
  category?: TemplateCategory;
  language?: string;
  search?: string;
}

/**
 * Helper para cores de status
 */
export const TemplateStatusColors: Record<TemplateStatus, string> = {
  PENDING: '#FFA500',    // Laranja
  APPROVED: '#22C55E',   // Verde
  REJECTED: '#EF4444',   // Vermelho
  DISABLED: '#6B7280',   // Cinza
  PAUSED: '#F59E0B'      // Amarelo
};

/**
 * Helper para labels de status
 */
export const TemplateStatusLabels: Record<TemplateStatus, string> = {
  PENDING: 'Em Análise',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  DISABLED: 'Desabilitado',
  PAUSED: 'Pausado'
};

/**
 * Helper para labels de categoria
 */
export const TemplateCategoryLabels: Record<TemplateCategory, string> = {
  MARKETING: 'Marketing',
  UTILITY: 'Utilidade',
  AUTHENTICATION: 'Autenticação'
};

/**
 * Helper para ícones de categoria
 */
export const TemplateCategoryIcons: Record<TemplateCategory, string> = {
  MARKETING: '📢',
  UTILITY: '🔧',
  AUTHENTICATION: '🔐'
};

/**
 * Idiomas suportados
 */
export const SupportedLanguages = [
  { code: 'pt_BR', label: 'Português (Brasil)' },
  { code: 'en_US', label: 'English (US)' },
  { code: 'es_ES', label: 'Español (España)' },
  { code: 'es_MX', label: 'Español (México)' }
];

// ============================================================================
// BUSINESS PROFILE TYPES
// ============================================================================

/**
 * Categorias de negócio disponíveis
 */
export type BusinessVertical = 
  | 'AUTOMOTIVE'
  | 'BEAUTY'
  | 'APPAREL'
  | 'EDU'
  | 'ENTERTAIN'
  | 'EVENT_PLAN'
  | 'FINANCE'
  | 'GROCERY'
  | 'GOVT'
  | 'HOTEL'
  | 'HEALTH'
  | 'NONPROFIT'
  | 'PROF_SERVICES'
  | 'RETAIL'
  | 'TRAVEL'
  | 'RESTAURANT'
  | 'OTHER';

/**
 * Status de aprovação do nome de exibição
 */
export type DisplayNameStatus = 'APPROVED' | 'PENDING' | 'REJECTED' | 'EXPIRED' | 'NONE';

/**
 * Perfil comercial do número WhatsApp
 */
export interface BusinessProfile {
  about?: string;              // Descrição curta (max 139 chars)
  address?: string;            // Endereço (max 256 chars)
  description?: string;        // Descrição detalhada (max 512 chars)
  email?: string;              // Email comercial
  profile_picture_url?: string; // URL da foto (read-only)
  websites?: string[];         // Array de websites (max 2)
  vertical?: BusinessVertical; // Categoria
  messaging_product: 'whatsapp';
  // Campos read-only
  name_status?: DisplayNameStatus; // Status de aprovação do nome de exibição
  display_phone_number?: string;   // Número formatado
}

/**
 * Dados para atualizar perfil
 */
export interface UpdateProfileData {
  about?: string;
  description?: string;
  email?: string;
  vertical?: BusinessVertical;
  address?: string;
  websites?: string[];
}

/**
 * Labels para as categorias
 */
export const BusinessVerticalLabels: Record<BusinessVertical, string> = {
  AUTOMOTIVE: 'Automotivo',
  BEAUTY: 'Beleza, Spa e Salão',
  APPAREL: 'Roupas e Acessórios',
  EDU: 'Educação',
  ENTERTAIN: 'Entretenimento',
  EVENT_PLAN: 'Planejamento de Eventos',
  FINANCE: 'Finanças',
  GROCERY: 'Supermercado',
  GOVT: 'Governo',
  HOTEL: 'Hotel e Hospedagem',
  HEALTH: 'Saúde',
  NONPROFIT: 'Organização sem Fins Lucrativos',
  PROF_SERVICES: 'Serviços Profissionais',
  RETAIL: 'Varejo',
  TRAVEL: 'Viagens e Transporte',
  RESTAURANT: 'Restaurante',
  OTHER: 'Outro'
};

/**
 * Opções para o dropdown de categorias
 */
export const BusinessVerticalOptions = Object.entries(BusinessVerticalLabels).map(
  ([value, label]) => ({ value: value as BusinessVertical, label })
);

